import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import type {
  AgentDetail,
  AgentVersionCreate,
  AgentVersionDetail,
  ExecutionDetail,
  SkillSummary,
} from '../../types/agent-studio';
import {
  AgentStatusPill,
  TraceLogExpandable,
} from '../../components/agent-studio';

type FormState = {
  prompt_template: string;
  system_instructions: string;
  model_provider: string;
  model_name: string;
  temperature: number;
  max_turns: number | null;
  memory_scope: 'project' | 'user' | 'local' | 'none';
  permission_mode: string;
  skills: string[];
  extra_config_text: string;
};

type TestState =
  | { kind: 'idle' }
  | { kind: 'launching' }
  | { kind: 'polling'; executionLogId: string; elapsedSeconds: number }
  | { kind: 'completed'; execution: ExecutionDetail }
  | { kind: 'failed'; error: string };

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

// ── Field helpers ─────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="t-eyebrow" style={{ marginBottom: '6px', color: 'var(--ink-secondary)' }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="t-eyebrow" style={{
      marginBottom: '12px',
      marginTop: '20px',
      color: 'var(--ink-primary)',
      borderBottom: '1px solid var(--border)',
      paddingBottom: '6px',
    }}>
      {children}
    </div>
  );
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: '13px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  background: 'white',
  boxSizing: 'border-box',
};

const MONO_TEXTAREA: React.CSSProperties = {
  ...INPUT_STYLE,
  fontFamily: "'Menlo', 'Consolas', 'Courier New', monospace",
  lineHeight: 1.5,
  resize: 'vertical',
};

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentStudioEdit() {
  const { system_name } = useParams<{ system_name: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sourceVersionId = searchParams.get('source_version_id');
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [allSkills, setAllSkills] = useState<SkillSummary[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [testState, setTestState] = useState<TestState>({ kind: 'idle' });
  const [saveState, setSaveState] = useState<SaveState>({ kind: 'idle' });
  const [loadError, setLoadError] = useState<string | null>(null);
  const [extraConfigError, setExtraConfigError] = useState<string | null>(null);
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [agentDetail, skillList] = await Promise.all([
          api.getAgent(system_name!),
          api.listSkills(),
        ]);
        if (cancelled) return;
        setAgent(agentDetail);
        setAllSkills(skillList.skills);

        let sourceVersion: AgentVersionDetail | null = null;
        if (sourceVersionId) {
          try {
            sourceVersion = await api.getAgentVersion(system_name!, sourceVersionId);
          } catch (err) {
            if (!cancelled) {
              setLoadError(`Не удалось загрузить версию ${sourceVersionId}: ${
                err instanceof Error ? err.message : String(err)
              }`);
            }
            return;
          }
        } else {
          sourceVersion = agentDetail.active_version;
        }

        if (cancelled) return;
        if (sourceVersion) {
          setForm({
            prompt_template: sourceVersion.prompt_template,
            system_instructions: sourceVersion.system_instructions,
            model_provider: sourceVersion.model_provider,
            model_name: sourceVersion.model_name,
            temperature: sourceVersion.temperature,
            max_turns: sourceVersion.max_turns,
            memory_scope: sourceVersion.memory_scope,
            permission_mode: sourceVersion.permission_mode,
            skills: sourceVersion.skills,
            extra_config_text: JSON.stringify(sourceVersion.extra_config, null, 2),
          });
        }
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      }
    }
    if (system_name) load();
    return () => { cancelled = true; };
  }, [system_name, sourceVersionId]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current !== null) {
        window.clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm(prev => prev ? { ...prev, [key]: value } : prev);
    if (key === 'extra_config_text') {
      try {
        JSON.parse((value as string) || '{}');
        setExtraConfigError(null);
      } catch {
        setExtraConfigError('Дополнительные параметры — невалидный JSON.');
      }
    }
  }

  function toggleSkill(systemName: string) {
    setForm(prev => {
      if (!prev) return prev;
      const has = prev.skills.includes(systemName);
      return {
        ...prev,
        skills: has
          ? prev.skills.filter(s => s !== systemName)
          : [...prev.skills, systemName],
      };
    });
  }

  function buildPayload(f: FormState): AgentVersionCreate | null {
    let extraConfig: Record<string, unknown> = {};
    try {
      extraConfig = JSON.parse(f.extra_config_text || '{}');
    } catch {
      return null;
    }
    return {
      prompt_template: f.prompt_template,
      system_instructions: f.system_instructions,
      model_provider: f.model_provider,
      model_name: f.model_name,
      temperature: f.temperature,
      max_turns: f.max_turns,
      memory_scope: f.memory_scope,
      permission_mode: f.permission_mode,
      skills: f.skills,
      extra_config: extraConfig,
    };
  }

  function startPolling(executionLogId: string) {
    const MAX_ELAPSED_SECONDS = 60;
    let elapsed = 0;

    if (pollIntervalRef.current !== null) {
      window.clearInterval(pollIntervalRef.current);
    }

    pollIntervalRef.current = window.setInterval(async () => {
      elapsed += 3;
      try {
        const exec = await api.getAgentExecution(system_name!, executionLogId);
        if (exec.status === 'completed' || exec.status === 'failed') {
          if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setTestState({ kind: 'completed', execution: exec });
          return;
        }
        setTestState({ kind: 'polling', executionLogId, elapsedSeconds: elapsed });
        if (elapsed >= MAX_ELAPSED_SECONDS) {
          if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setTestState({
            kind: 'failed',
            error: `Тест выполняется дольше ${MAX_ELAPSED_SECONDS}с. Проверьте журнал запусков позже.`,
          });
        }
      } catch (err) {
        if (pollIntervalRef.current !== null) {
          window.clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setTestState({
          kind: 'failed',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }, 3000);
  }

  async function handleTest() {
    if (!form) return;
    setTestState({ kind: 'launching' });

    const payload = buildPayload(form);
    if (!payload) {
      setTestState({ kind: 'failed', error: 'Дополнительные параметры — невалидный JSON.' });
      return;
    }

    try {
      const draft = await api.createAgentVersion(system_name!, payload);
      const fixtureName = `${system_name}_default`;
      const response = await api.testAgentVersion(system_name!, draft.id, fixtureName);
      setTestState({ kind: 'polling', executionLogId: response.execution_log_id, elapsedSeconds: 0 });
      startPolling(response.execution_log_id);
    } catch (err) {
      setTestState({ kind: 'failed', error: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleSaveDraft() {
    if (!form) return;
    setSaveState({ kind: 'saving' });

    const payload = buildPayload(form);
    if (!payload) {
      setSaveState({ kind: 'error', message: 'Дополнительные параметры — невалидный JSON.' });
      return;
    }

    try {
      const draft = await api.createAgentVersion(system_name!, payload);
      navigate(`/agents/${system_name}/versions/${draft.id}`);
    } catch (err) {
      setSaveState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function handleActivate() {
    if (!form) return;
    setSaveState({ kind: 'saving' });

    const payload = buildPayload(form);
    if (!payload) {
      setSaveState({ kind: 'error', message: 'Дополнительные параметры — невалидный JSON.' });
      return;
    }

    try {
      const draft = await api.createAgentVersion(system_name!, payload);
      await api.activateAgentVersion(system_name!, draft.id);
      navigate(`/agents/${system_name}`);
    } catch (err) {
      setSaveState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }

  const isBusy = testState.kind === 'launching' || testState.kind === 'polling';
  const isSaving = saveState.kind === 'saving';

  // ── Loading / error states ───────────────────────────────────────────────────

  if (loadError) {
    return (
      <div style={{ padding: '24px' }}>
        <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)' }}>
          <Link to={`/agents/${system_name}`} style={{ color: 'inherit' }}>Студия агентов</Link>
          {' · '}Новая версия
        </div>
        <div className="card" style={{ marginTop: '24px', padding: '32px', borderLeft: '3px solid var(--red)' }}>
          <div className="t-eyebrow" style={{ color: 'var(--red)' }}>Ошибка загрузки</div>
          <p className="t-body" style={{ marginTop: '8px' }}>
            Не удалось загрузить агента {system_name}: {loadError}
          </p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div style={{ padding: '24px', paddingBottom: '80px' }}>
        <div style={{ height: '14px', background: 'var(--bg-subtle)', width: '260px', borderRadius: '4px', opacity: 0.4 }} />
        <div style={{ height: '28px', background: 'var(--bg-subtle)', width: '380px', borderRadius: '4px', opacity: 0.4, marginTop: '12px', marginBottom: '24px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '60% 40%', gap: '24px', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="card" style={{ padding: '20px', opacity: 0.4 }}>
              <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '140px', borderRadius: '4px', marginBottom: '10px' }} />
              <div style={{ background: 'var(--bg-subtle)', height: '300px', borderRadius: '4px' }} />
            </div>
            <div className="card" style={{ padding: '20px', opacity: 0.4 }}>
              <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '120px', borderRadius: '4px', marginBottom: '10px' }} />
              <div style={{ background: 'var(--bg-subtle)', height: '200px', borderRadius: '4px' }} />
            </div>
            <div className="card" style={{ padding: '20px', opacity: 0.4 }}>
              <div style={{ background: 'var(--bg-subtle)', height: '12px', width: '180px', borderRadius: '4px', marginBottom: '10px' }} />
              <div style={{ background: 'var(--bg-subtle)', height: '120px', borderRadius: '4px' }} />
            </div>
          </div>
          <div className="card" style={{ padding: '20px', opacity: 0.4, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[100, 140, 60, 60, 80, 60].map((w, i) => (
              <div key={i} style={{ background: 'var(--bg-subtle)', height: '12px', width: w, borderRadius: '4px' }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Full form render ─────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px', paddingBottom: '80px' }}>
      {/* Breadcrumb */}
      <div className="t-eyebrow" style={{ color: 'var(--ink-secondary)' }}>
        <Link to="/agents" style={{ color: 'inherit' }}>Студия агентов</Link>
        {' · '}
        <Link to={`/agents/${system_name}`} style={{ color: 'inherit' }}>{system_name}</Link>
        {' · '}
        {sourceVersionId ? 'РЕДАКТИРОВАНИЕ ЧЕРНОВИКА' : 'НОВАЯ ВЕРСИЯ'}
      </div>

      {/* Header */}
      <h1 className="t-display" style={{ marginTop: '8px', marginBottom: '4px' }}>
        Редактирование агента: <span style={{ fontFamily: 'var(--font-mono)' }}>{system_name}</span>
      </h1>
      <p className="t-body-sm" style={{ color: 'var(--ink-secondary)', margin: '0 0 24px 0' }}>
        {sourceVersionId ? 'Редактирование черновика' : 'Создание новой версии'}
        {agent?.active_version && (
          <span style={{ marginLeft: '8px' }}>
            <AgentStatusPill status="active" />
            {' '}текущая: v{agent.active_version.version_number}
          </span>
        )}
      </p>

      {/* Two-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '60% 40%', gap: '24px', alignItems: 'start' }}>

        {/* Left column — long-text fields */}
        <div>
          <div style={{ marginBottom: '16px' }}>
            <FieldLabel>Системные инструкции</FieldLabel>
            <textarea
              value={form.system_instructions}
              onChange={e => setField('system_instructions', e.target.value)}
              style={{ ...MONO_TEXTAREA, minHeight: '300px' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <FieldLabel>Шаблон промпта</FieldLabel>
            <textarea
              value={form.prompt_template}
              onChange={e => setField('prompt_template', e.target.value)}
              style={{ ...MONO_TEXTAREA, minHeight: '200px' }}
            />
          </div>
          <div>
            <FieldLabel>Дополнительные параметры (JSON)</FieldLabel>
            <textarea
              value={form.extra_config_text}
              onChange={e => setField('extra_config_text', e.target.value)}
              style={{
                ...MONO_TEXTAREA,
                minHeight: '120px',
                borderColor: extraConfigError ? 'var(--red)' : undefined,
              }}
            />
            {extraConfigError && (
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--red)' }}>
                {extraConfigError}
              </p>
            )}
          </div>
        </div>

        {/* Right column — sticky sidebar */}
        <div style={{ position: 'sticky', top: '16px' }}>
          <div className="card" style={{ padding: '20px' }}>

            {/* Модель */}
            <SectionTitle>Модель</SectionTitle>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Провайдер</FieldLabel>
              <select
                value={form.model_provider}
                onChange={e => setField('model_provider', e.target.value)}
                style={INPUT_STYLE}
              >
                <option value="openai">openai</option>
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Модель</FieldLabel>
              <input
                type="text"
                value={form.model_name}
                onChange={e => setField('model_name', e.target.value)}
                style={INPUT_STYLE}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Температура</FieldLabel>
              <input
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={form.temperature}
                onChange={e => setField('temperature', parseFloat(e.target.value) || 0)}
                style={INPUT_STYLE}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Макс. шагов</FieldLabel>
              <input
                type="number"
                value={form.max_turns ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  setField('max_turns', v === '' ? null : parseInt(v, 10));
                }}
                style={INPUT_STYLE}
                placeholder="без ограничений"
              />
            </div>

            {/* Память и доступ */}
            <SectionTitle>Память и доступ</SectionTitle>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Область памяти</FieldLabel>
              <select
                value={form.memory_scope}
                onChange={e => setField('memory_scope', e.target.value as FormState['memory_scope'])}
                style={INPUT_STYLE}
              >
                <option value="project">project</option>
                <option value="user">user</option>
                <option value="local">local</option>
                <option value="none">none</option>
              </select>
            </div>
            <div style={{ marginBottom: '12px' }}>
              <FieldLabel>Режим доступа</FieldLabel>
              <input
                type="text"
                value={form.permission_mode}
                onChange={e => setField('permission_mode', e.target.value)}
                style={INPUT_STYLE}
              />
            </div>

            {/* Навыки */}
            <SectionTitle>Навыки</SectionTitle>
            {allSkills.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--ink-tertiary)', margin: 0 }}>Навыки не загружены.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {allSkills.map(skill => (
                  <label
                    key={skill.system_name}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px' }}
                  >
                    <input
                      type="checkbox"
                      checked={form.skills.includes(skill.system_name)}
                      onChange={() => toggleSkill(skill.system_name)}
                      style={{ marginTop: '2px', flexShrink: 0 }}
                    />
                    <span>
                      <span style={{ color: 'var(--ink-primary)' }}>{skill.display_name}</span>
                      <span style={{ color: 'var(--ink-tertiary)', marginLeft: '4px', fontSize: '11px' }}>
                        ({skill.skill_type})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Test result panel */}
      {testState.kind !== 'idle' && (
        <div style={{ marginTop: '32px' }}>
          <div className="t-eyebrow" style={{ marginBottom: '12px' }}>РЕЗУЛЬТАТ ТЕСТА</div>
          {testState.kind === 'launching' && (
            <div className="card" style={{ padding: '20px', color: 'var(--ink-secondary)', fontSize: '14px' }}>
              Запуск теста…
            </div>
          )}
          {testState.kind === 'polling' && (
            <div className="card" style={{ padding: '20px', border: '1px dashed var(--border-strong)', fontSize: '14px', color: 'var(--ink-secondary)' }}>
              Тест выполняется… ({testState.elapsedSeconds}с)
            </div>
          )}
          {testState.kind === 'completed' && (
            <TraceLogExpandable execution={testState.execution} defaultExpanded={true} />
          )}
          {testState.kind === 'failed' && (
            <div className="card" style={{ padding: '20px', borderLeft: '3px solid var(--red)' }}>
              <div className="t-eyebrow" style={{ color: 'var(--red)', marginBottom: '8px' }}>Ошибка теста</div>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--ink-secondary)' }}>
                {testState.error}
              </p>
              <button
                onClick={() => setTestState({ kind: 'idle' })}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  padding: '6px 14px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--ink-secondary)',
                }}
              >
                Попробовать снова
              </button>
            </div>
          )}
        </div>
      )}

      {/* Sticky action bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid var(--border)',
        padding: '12px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate(`/agents/${system_name}`)}
          style={{
            background: 'transparent',
            border: '1px solid var(--border)',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            color: 'var(--ink-secondary)',
          }}
        >
          ← Отменить
        </button>

        <div style={{ flex: 1 }} />

        {saveState.kind === 'error' && (
          <span style={{ fontSize: '12px', color: 'var(--red)', maxWidth: '300px' }}>
            {saveState.message}
          </span>
        )}

        <button
          onClick={handleTest}
          disabled={isBusy || isSaving}
          style={{
            background: 'transparent',
            border: '1px solid var(--navy)',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: isBusy || isSaving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            color: 'var(--navy)',
            opacity: isBusy || isSaving ? 0.5 : 1,
          }}
        >
          Тестировать
        </button>

        <button
          onClick={handleSaveDraft}
          disabled={isSaving}
          style={{
            background: 'transparent',
            border: '1px solid var(--navy)',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            color: 'var(--navy)',
            fontWeight: 600,
            opacity: isSaving ? 0.5 : 1,
          }}
        >
          {isSaving ? 'Сохранение…' : 'Сохранить как черновик'}
        </button>

        <button
          onClick={handleActivate}
          disabled={isSaving}
          style={{
            background: isSaving ? 'var(--ink-tertiary)' : 'var(--navy)',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            color: 'white',
            fontWeight: 600,
          }}
        >
          {isSaving ? 'Сохранение…' : 'Активировать'}
        </button>
      </div>
    </div>
  );
}
